/**
 * Time Entry Modal
 * Modal for creating/editing time entries
 */

import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { useTimeStore } from '../store/timeStore';
import { useClientStore } from '../store/clientStore';
import './Modal.css';

const TimeEntryModal = ({ entry, onClose }) => {
  const user = useAuthStore((state) => state.user);
  const createEntry = useTimeStore((state) => state.createEntry);
  const updateEntry = useTimeStore((state) => state.updateEntry);
  const clients = useClientStore((state) => state.clients);

  const [formData, setFormData] = useState({
    clientId: entry?.clientId || '',
    date: entry?.startTime?.toDate()?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0],
    startTime: entry?.startTime?.toDate()?.toTimeString().slice(0, 5) || '09:00',
    endTime: entry?.endTime?.toDate()?.toTimeString().slice(0, 5) || '17:00',
    description: entry?.description || '',
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Parse date and times
      const startDateTime = new Date(`${formData.date}T${formData.startTime}`);
      const endDateTime = new Date(`${formData.date}T${formData.endTime}`);

      if (endDateTime <= startDateTime) {
        setError('End time must be after start time');
        setLoading(false);
        return;
      }

      if (entry) {
        // Update existing entry
        await updateEntry(entry.id, {
          clientId: formData.clientId,
          startTime: startDateTime,
          endTime: endDateTime,
          description: formData.description,
        });
      } else {
        // Create new entry
        await createEntry(user.uid, {
          clientId: formData.clientId,
          startTime: startDateTime,
          endTime: endDateTime,
          description: formData.description,
        });
      }

      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{entry ? 'Edit Time Entry' : 'Add Time Entry'}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
          <div className="form-group">
            <label htmlFor="client">Client *</label>
            <select
              id="client"
              className="input"
              value={formData.clientId}
              onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
              required
            >
              <option value="">Select Client</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="date">Date *</label>
            <input
              id="date"
              type="date"
              className="input"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="startTime">Start Time *</label>
              <input
                id="startTime"
                type="time"
                className="input"
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="endTime">End Time *</label>
              <input
                id="endTime"
                type="time"
                className="input"
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              className="input"
              rows="3"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="What did you work on?"
            />
          </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : entry ? 'Update Entry' : 'Create Entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TimeEntryModal;
